import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";
import { NFCCard, NFCCardFilter, nfcCardAPI } from "../services/nfcCardAPI";

const EXPORT_PAGE_SIZE = 200;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

export type NfcCardExportFilters = Omit<NFCCardFilter, "page" | "limit" | "pageNo" | "pageSize"> & {
  branchId?: string;
  holderId?: string | null;
};

export async function fetchAllNfcCards(
  filters: NfcCardExportFilters
): Promise<NFCCard[]> {
  if (filters.holderId) {
    const result = await nfcCardAPI.getCardsByHolder(filters.holderId);
    return result.cards || [];
  }

  const { holderId: _omit, ...listFilters } = filters;
  const all: NFCCard[] = [];
  let page = 1;
  let total = 0;

  do {
    const { cards, total: count } = await nfcCardAPI.getCards({
      ...listFilters,
      page,
      limit: EXPORT_PAGE_SIZE,
    });
    total = count;
    all.push(...cards);
    page += 1;
  } while (all.length < total);

  return all;
}

type ExportRow = {
  sno: number;
  cardNumber: string;
  balance: string;
  holder: string;
  phone: string;
  status: string;
  assignment: string;
  createdAt: string;
};

function toExportRows(cards: NFCCard[]): ExportRow[] {
  return cards.map((c, index) => ({
    sno: index + 1,
    cardNumber: c.displayNumber || "—",
    balance: formatCurrency(c.balance),
    holder: c.holder?.holderName || "—",
    phone: c.holder?.phone || "—",
    status: c.status || "—",
    assignment: c.isAssigned ? "Assigned" : "Unassigned",
    createdAt: c.createdAt
      ? format(new Date(c.createdAt), "dd MMM yyyy")
      : "—",
  }));
}

function escapeCsvCell(value: string): string {
  const s = String(value ?? "");
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

const HEADERS = [
  "S.No.",
  "Card Number",
  "Balance",
  "Holder",
  "Phone",
  "Status",
  "Assignment",
  "Created",
];

export function exportNfcCardsExcel(cards: NFCCard[]): void {
  const rows = toExportRows(cards);
  const lines = [
    HEADERS.join(","),
    ...rows.map((r) =>
      [
        r.sno,
        r.cardNumber,
        r.balance,
        r.holder,
        r.phone,
        r.status,
        r.assignment,
        r.createdAt,
      ]
        .map((v) => escapeCsvCell(String(v)))
        .join(",")
    ),
  ];
  const stamp = format(new Date(), "yyyy-MM-dd_HHmm");
  downloadBlob(
    new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" }),
    `nfc-cards_${stamp}.csv`
  );
}

export function exportNfcCardsPdf(cards: NFCCard[]): void {
  const rows = toExportRows(cards);
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const stamp = format(new Date(), "dd MMM yyyy, HH:mm");

  doc.setFontSize(14);
  doc.text("NFC Cards", 40, 36);
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text(`Exported ${stamp} · ${cards.length} record(s)`, 40, 52);
  doc.setTextColor(0);

  autoTable(doc, {
    startY: 64,
    head: [HEADERS],
    body: rows.map((r) => [
      r.sno,
      r.cardNumber,
      r.balance,
      r.holder,
      r.phone,
      r.status,
      r.assignment,
      r.createdAt,
    ]),
    styles: { fontSize: 8, cellPadding: 4 },
    headStyles: { fillColor: [41, 98, 255] },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: 24, right: 24 },
  });

  doc.save(`nfc-cards_${format(new Date(), "yyyy-MM-dd_HHmm")}.pdf`);
}
